import type { TFunction } from "i18next";
import { keyFromSelector } from "i18next";
import type { FormApi } from "../../components/form/createForm.ts";
import { createForm } from "../../components/form/createForm.ts";
import { Form } from "../../components/form/Form.tsx";
import { check, forward, type InferOutput, object, optional, partialCheck, pipe, string } from "valibot";
import { errorTranslation, requireText } from "../../utils/validation/base.ts";
import { MAX_CHARACTERS_SM, MINIMUM_ACTION_LOADING_DELAY } from "../../constants.ts";
import { TextInput } from "../../components/inputs/TextInput.tsx";
import { useTranslation } from "../../state/i18n.tsx";
import type { StepFormProps } from "./OnboardingPage.tsx";
import { Dynamic } from "solid-js/web";
import { SingleSelectInput } from "../../components/menu/Select.tsx";
import type { SelectOption } from "../../types/Menu.ts";
import Section from "../Section.tsx";
import PageHeader from "../PageHeader.tsx";
import { useUserState } from "../../state/user.tsx";
import { createSignal, Show } from "solid-js";
import { useCustomerState } from "../../state/customer.tsx";
import ApiService from "../../services/ApiService.ts";
import pMinDelay from "p-min-delay";
import { formatNumber } from "../../utils/numberUtil.ts";
import { makePersisted } from "@solid-primitives/storage";
import { isEmpty } from "lodash-es";

export const CompanySchema = pipe(
  object({
    name: requireText(MAX_CHARACTERS_SM),
    sector: requireText(MAX_CHARACTERS_SM),
    sectorOther: optional(string()),
    size: requireText(MAX_CHARACTERS_SM),
  }),
  // sectorOther is only shown (and saved) for OTHER, so a hidden leftover value must not block the submit
  forward(
    check(
      ({ sector, sectorOther }) => sector !== "OTHER" || !isEmpty(sectorOther),
      errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY)),
    ),
    ["sectorOther"],
  ),
  forward(
    partialCheck(
      [["sector"], ["sectorOther"]],
      ({ sector, sectorOther }) => sector !== "OTHER" || (sectorOther?.length ?? 0) <= MAX_CHARACTERS_SM,
      errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH)),
    ),
    ["sectorOther"],
  ),
);

export const getCompanySectors = (t: TFunction) => [
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_CONSTRUCTION), value: "CONSTRUCTION" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_INSTALLATION), value: "INSTALLATION" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_ENERGY_UTILITIES), value: "ENERGY_UTILITIES" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_MANUFACTURING), value: "MANUFACTURING" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_FACILITIES_SERVICES), value: "FACILITIES_SERVICES" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_SAFETY), value: "SAFETY" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_AUTOMOTIVE), value: "AUTOMOTIVE" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_INFRASTRUCTURE), value: "INFRASTRUCTURE" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_REAL_ESTATE), value: "REAL_ESTATE" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_LOGISTICS), value: "LOGISTICS" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_IT), value: "IT" },
  { label: t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_OTHER), value: "OTHER" },
];

const CompanyForm = (props: StepFormProps) => {
  const { t, currentLanguage } = useTranslation();
  const customer = useCustomerState();
  const user = useUserState();
  const [size, setSize] = makePersisted(createSignal<string>(), { name: "size" });

  const [showWarning, setShowWarning] = createSignal(false);

  const updateCustomerName = ApiService.updateCustomerName();
  const updateCustomerSettings = ApiService.updateCustomerSettings();

  const ranges = [
    { from: 1, to: 10 },
    { from: 11, to: 50 },
    { from: 51, to: 200 },
    { from: 201, to: 1000 },
    { from: 1001, to: 5000 },
    { from: 5000, to: Infinity },
  ];

  const companySizes: SelectOption<string>[] = ranges.map((range) => {
    const from = formatNumber(currentLanguage(), range.from);
    const to = formatNumber(currentLanguage(), range.to);
    const label = range.to === Infinity ? `>${from}` : `${from}-${to}`;
    return { variant: "text", label, value: `${range.from}-${range.to}` };
  });

  // Signup stores the email address as company name and OTHER as sector as placeholders, so start those empty.
  // The form can't save OTHER without sectorOther, so that combination is always the placeholder.
  const hasPlaceholderName = customer.name.toLowerCase() === user().emailAddress.toLowerCase();
  const hasPlaceholderSector = customer.settings.segment === "OTHER" && isEmpty(customer.settings.otherSegment);

  const form = createForm<InferOutput<typeof CompanySchema>>(
    CompanySchema,
    async ({ name, sector, sectorOther, size }) => {
      setSize(size);
      await pMinDelay(
        Promise.all([
          updateCustomerName.mutateAsync(name),
          updateCustomerSettings.mutateAsync({
            ...customer.settings,
            segment: sector,
            otherSegment: sector === "OTHER" ? sectorOther : undefined,
          }),
        ]),
        MINIMUM_ACTION_LOADING_DELAY,
      )
        .then(props.next)
        .catch(() => setShowWarning(true));
    },
    {
      name: hasPlaceholderName ? undefined : customer.name,
      sector: hasPlaceholderSector ? undefined : customer.settings.segment,
      sectorOther: customer.settings.otherSegment ?? undefined,
      size: size(),
    },
  );

  return (
    <>
      <PageHeader
        title={t(($) => $.ONBOARDING_PAGE_COMPANY_WELCOME, { firstName: user().firstName })}
        subTitle={t(($) => $.ONBOARDING_PAGE_COMPANY_SETUP_ACCOUNT)}
        user={user()}
      />
      <Section fullHeight>
        <h2 class="mb-6 text-header-xl text-primary">{t(($) => $.ONBOARDING_PAGE_COMPANY_TITLE)}</h2>
        <Form form={form} class="flex size-full grow flex-col">
          {({ value, error, updateValue }) => (
            <div class="flex h-full grow flex-col">
              <div class="flex w-full flex-col gap-y-6">
                <TextInput
                  label={t(($) => $.ONBOARDING_PAGE_COMPANY_NAME)}
                  required
                  value={value("name")}
                  errorMessage={error("name")}
                  onInput={(e) => updateValue("name", e.target.value)}
                  onBlur={(e) => updateValue("name", e.target.value)}
                />
                <SingleSelectInput<string>
                  label={t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR)}
                  options={getCompanySectors(t).map((sector) => ({
                    variant: "text",
                    value: sector.value,
                    label: sector.label,
                  }))}
                  required
                  value={value("sector")}
                  errorMessage={error("sector")}
                  onSelect={(value) => updateValue("sector", value)}
                  sameWidth
                />
                <Show when={value("sector") === "OTHER"}>
                  <TextInput
                    label={t(($) => $.ONBOARDING_PAGE_COMPANY_SECTOR_ADDITIONAL)}
                    value={value("sectorOther")}
                    required
                    errorMessage={error("sectorOther")}
                    onInput={(e) => updateValue("sectorOther", e.target.value)}
                    onBlur={(e) => updateValue("sectorOther", e.target.value)}
                  />
                </Show>
                <SingleSelectInput<string>
                  label={t(($) => $.ONBOARDING_PAGE_COMPANY_SIZE)}
                  options={companySizes}
                  required
                  value={value("size")}
                  errorMessage={error("size")}
                  onSelect={(value) => updateValue("size", value)}
                  sameWidth
                />
              </div>
              <Dynamic
                component={props.navigation}
                form={form as unknown as FormApi<never>}
                back={props.back}
                showWarning={showWarning()}
              />
            </div>
          )}
        </Form>
      </Section>
    </>
  );
};

export default CompanyForm;
